import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

export function SignupPage() {
  const { signUp } = useAuth()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [roles, setRoles] = useState(['family_caregiver'])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  function toggleRole(value) {
    setRoles(prev => prev.includes(value)
      ? prev.filter(r => r !== value)
      : [...prev, value]
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)

    if (roles.length === 0) {
      setError('Please select at least one role that applies to you')
      return
    }

    setLoading(true)

    try {
      await signUp(email, password, name, roles)
      setSuccess(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <AuthLayout title="Check your email">
        <p style={{ color: '#4A5568', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          We&rsquo;ve sent a confirmation link to <strong>{email}</strong>. Click the link to activate your account, then come back and sign in.
        </p>
        <Link to="/login" style={linkStyle}>Back to sign in</Link>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Create your account">
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="name" style={labelStyle}>Your name</label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={inputStyle}
            autoComplete="name"
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="email" style={labelStyle}>Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={inputStyle}
            autoComplete="email"
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="password" style={labelStyle}>Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            style={inputStyle}
            autoComplete="new-password"
          />
          <p style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
            At least 8 characters
          </p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={labelStyle}>Which of these apply to you?</label>
          <p style={{ fontSize: '12px', color: '#718096', marginTop: '-4px', marginBottom: '10px' }}>
            Select all that apply. You can update this later in Settings.
          </p>
          <div style={{ display: 'grid', gap: '8px' }}>
            <RoleCheckbox
              value="paid_aide"
              checked={roles.includes('paid_aide')}
              onChange={() => toggleRole('paid_aide')}
              label="I work as a paid caregiver"
              description="Employed by an agency or private client"
            />
            <RoleCheckbox
              value="family_caregiver"
              checked={roles.includes('family_caregiver')}
              onChange={() => toggleRole('family_caregiver')}
              label="I care for a family member"
              description="Paid or unpaid family caregiving"
            />
            <RoleCheckbox
              value="care_coordinator"
              checked={roles.includes('care_coordinator')}
              onChange={() => toggleRole('care_coordinator')}
              label="I coordinate care across multiple caregivers"
              description="Overseeing a loved one's total scope of care"
            />
            <RoleCheckbox
              value="caregiver_advocate"
              checked={roles.includes('caregiver_advocate')}
              onChange={() => toggleRole('caregiver_advocate')}
              label="I help other caregivers"
              description="Advocate, social worker, or support role"
            />
          </div>
        </div>

        {error && (
          <div style={errorStyle}>{error}</div>
        )}

        <button type="submit" disabled={loading} style={primaryButtonStyle}>
          {loading ? 'Creating account...' : 'Create account'}
        </button>

        <p style={{ marginTop: '1rem', fontSize: '14px', color: '#4A5568' }}>
          Already have an account? <Link to="/login" style={linkStyle}>Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  )
}

function AuthLayout({ title, children }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      background: '#F7FAFC',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: '#FFFFFF',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
      }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: '#2C7A7B',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FFFFFF',
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '20px',
            fontWeight: 500,
            marginBottom: '1rem',
          }}>K</div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '24px',
            fontWeight: 500,
            color: '#1A202C',
          }}>{title}</h1>
        </div>
        {children}
      </div>
    </div>
  )
}

function RoleCheckbox({ value, checked, onChange, label, description }) {
  return (
    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '12px',
      border: `1px solid ${checked ? '#2C7A7B' : '#E2E8F0'}`,
      borderRadius: '8px',
      cursor: 'pointer',
      background: checked ? '#E1F5EE' : '#FFFFFF',
      transition: 'all 0.15s',
    }}>
      <input
        type="checkbox"
        value={value}
        checked={checked}
        onChange={onChange}
        style={{ marginTop: '2px', flexShrink: 0 }}
      />
      <div>
        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', marginBottom: '2px' }}>
          {label}
        </div>
        <div style={{ fontSize: '12px', color: '#718096', lineHeight: 1.4 }}>
          {description}
        </div>
      </div>
    </label>
  )
}

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#4A5568',
  marginBottom: '6px',
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #E2E8F0',
  borderRadius: '6px',
  fontSize: '15px',
  background: '#FFFFFF',
}

const radioLabelStyle = {
  flex: 1,
  padding: '10px 12px',
  border: '1px solid #E2E8F0',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  background: '#FFFFFF',
}

const radioLabelActiveStyle = {
  borderColor: '#2C7A7B',
  background: '#E1F5EE',
  color: '#005440',
  fontWeight: 500,
}

const primaryButtonStyle = {
  width: '100%',
  padding: '11px',
  background: '#2C7A7B',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  fontSize: '15px',
  fontWeight: 500,
  cursor: 'pointer',
}

const linkStyle = {
  color: '#2C7A7B',
  textDecoration: 'none',
  fontWeight: 500,
}

const errorStyle = {
  padding: '10px 12px',
  background: '#FAECE7',
  color: '#993C1D',
  borderRadius: '6px',
  fontSize: '13px',
  marginBottom: '1rem',
}
